/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import AuthService from 'cio/services/auth';
import RouterService from '@ember/routing/router-service';
import AccountModel from 'cio/models/account';
import EventBusService, { MAIN_NAV_COLLAPSE, MAIN_NAV_EXPAND } from 'cio/services/event-bus';
import CustomFormsService from 'cio/services/custom-forms';
import FeaturesService from 'ember-feature-flags/services/features';
import EnvironmentModel from 'cio/models/environment';
import { EnvironmentOption } from 'cio/pods/app/controller';
import EnvironmentService from 'cio/services/environment';
import DS from 'ember-data';
import AccountService from 'cio/services/account';
import { sortByCaseInsensitive } from 'cio/utils/sort';

interface MainNavArgs {
  collapse: boolean;
  forceCollapse: boolean;
  environmentId: string;
  account: AccountModel;
  environment?: EnvironmentModel;
  environments: EnvironmentOption[];
  onSetEnvironment: () => unknown;
}

export default class MainNavComponent extends Component<MainNavArgs> {
  @service auth!: AuthService;
  @service router!: RouterService;
  @service eventBus!: EventBusService;
  @service customForms!: CustomFormsService;
  @service features!: FeaturesService;
  @service environment!: EnvironmentService;
  @service store!: DS.Store;
  @service account!: AccountService;

  @tracked preventCollapse = false;
  @tracked integrationsActiveButNotForms = false;

  @tracked isMouseIn = false;

  mouseEnterEvent = MAIN_NAV_EXPAND;
  mouseLeaveEvent = MAIN_NAV_COLLAPSE;

  @computed('router.currentRouteName')
  get isCampaignOverview() {
    let { currentRouteName } = this.router;
    return (
      currentRouteName.includes('campaigns.campaign.overview') ||
      currentRouteName.includes('broadcasts.broadcast.overview')
    );
  }

  @computed('router.currentRouteName')
  get dashboardOrSetupGuideActive() {
    return ['dashboard', 'setup-guide'].any((routeName) => this.router.isActive(routeName));
  }

  @computed('router.currentRouteName')
  get isDataActive() {
    let dataIntegrationsRoutes = ['data-index', 'imports', 'exports', 'integrations'];

    return dataIntegrationsRoutes.any((routeName) => this.router.isActive(routeName));
  }

  @computed('router.currentRouteName')
  get isCustomObjectsActive() {
    return this.router.currentRouteName.includes('objects.type');
  }

  @computed('router.currentRouteName')
  get isContentActive() {
    return ['message-library', 'assets', 'layouts', 'snippets', 'collections', 'in-app-messages'].any((routeName) =>
      this.router.isActive(routeName),
    );
  }

  @computed('router.currentRouteName', 'isContentActive')
  get isSettingsActive() {
    // Snippets are nested in settings, and snippets are in the Content bucket. Don't make Settings active if Snippets
    // are the active route
    if (this.isContentActive) {
      return false;
    }
    return ['workspace-settings', 'settings'].any((routeName) => this.router.isActive(routeName));
  }

  @action
  onSubMenuToggle(event: 'open' | 'close') {
    if (!this.isMouseIn) {
      if (event === 'open') {
        this.eventBus.publish(MAIN_NAV_EXPAND, MAIN_NAV_EXPAND);
      } else {
        this.eventBus.publish(MAIN_NAV_COLLAPSE, MAIN_NAV_COLLAPSE);
      }
    }
    this.preventCollapse = event === 'open';
  }

  @action
  publishEvent(event: typeof MAIN_NAV_EXPAND | typeof MAIN_NAV_COLLAPSE) {
    this.isMouseIn = event === MAIN_NAV_EXPAND;

    if (!this.preventCollapse) {
      this.eventBus.publish(event, event);
    }
  }

  get showFullMenu() {
    // if masqueraded into an account
    if (this.args.account.isMasqueraded) {
      // if the user doesn't have all permissions properly set to masq return false
      if ((this.auth.user?.noMasqAccess ?? false) || (this.auth.user?.masqLevelNotSet ?? false)) {
        return false;
      }
      // if the user has masq permissions and accessGrantedUntil is set return true
      if (
        (this.auth.user?.isSuperUser ?? false) ||
        (this.auth.user?.canReadWriteMasq ?? false) ||
        (this.auth.user?.canReadOnlyMasq ?? false)
      ) {
        return true;
      }
      return false;
    }
    return true;
  }

  get objectTypes() {
    return sortByCaseInsensitive(
      this.store
        .peekAll('object-type')
        .toArray()
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        .filter((ot) => !ot.isNew),
      ['name'],
    );
  }

  get showObjectTypes() {
    return this.objectTypes.length > 0;
  }

  get showObjectTypeDropdown() {
    return this.objectTypes.length > 3;
  }

  get lastEnvironmentId() {
    return this.args.environmentId ?? this.environment.lastEnvironmentId;
  }

  get showPremiumLabel() {
    return this.account.planFeatures.collections.showPremiumLabel;
  }
}
